/**
* Copyright ToolBelt Data Inc., 2020 All Rights Reserved
*/

import React, {useLayoutEffect, useRef, useState} from 'react';
import { useDispatch, useSelector } from "react-redux";

import classNames from "classnames";

import { map } from 'lodash';

// Actions & Icons
import { removeFilter } from "../universeSlice";
import { resolveEffectiveScore } from "../universeQuerySlice";
import LinkButton from "../../../common/components/LinkButton";
import FilterChip from "./components/FilterChip";
import { SEARCH_TYPE_FILTER_IDS } from "./searchTypeFilterIds";

// Config (Optional: For Map ID to Label)
// import FilterConfig from '../FilterConfig';

export const AppliedFilters = () => {
    const dispatch = useDispatch();

    // 1. Get Current Universe
    const currentUniverse = useSelector(state => state.universe.currentUniverse);

    // 2. Get data Filter of Universe
    const universeData = useSelector(state => state.universe.universes[currentUniverse]);
    const filters = universeData ? universeData.entities : {};
    const restrictions = universeData?.subscription?.restrictions;
    // SCORE pill reflects the active accuracy grade (set by the SCORE dropdown),
    // falling back to the subscription score. Mirrors the cube query in
    // universeQuerySlice so the displayed score matches what's filtered.
    const accuracyData = universeData?.accuracyData;

    // Handler for Deleted Filter
    const handleRemove = (id) => {
        dispatch(removeFilter(id));
    };

    // Discovery search-type pills represent search scope, not applied filters,
    // so they shouldn't appear as chips here even when stored in entities.
    const filterList = Object.entries(filters)
        .filter(([id]) => !SEARCH_TYPE_FILTER_IDS.has(id))
        .map(([id, data]) => ({
            id,
            ...data
        }));

    // Build a scope suffix to inline into the search-text chip when the user
    // has narrowed pill selection (i.e. fewer than all pills active). Pills
    // default to on, so only `value: false` entries are explicitly off.
    const offScopeIds = new Set(
        Object.entries(filters)
            .filter(([id, data]) => SEARCH_TYPE_FILTER_IDS.has(id) && data?.value === false)
            .map(([id]) => id)
    );
    const activeScopeIds = [...SEARCH_TYPE_FILTER_IDS].filter(id => !offScopeIds.has(id));
    const scopeSuffix = activeScopeIds.length < SEARCH_TYPE_FILTER_IDS.size && activeScopeIds.length > 0
        ? ` (in ${activeScopeIds.map(toTitleCase).join(', ')})`
        : '';

    // (Optional) For Locked Filters etc. > Score
    // if (currentUniverse === 'BUSINESS') { ... }
    return (
        <FilterBox>
            {createAppliedFilters(filterList, handleRemove, restrictions, scopeSuffix, accuracyData)}
        </FilterBox>
    );
};

// "REVIEWS" -> "Reviews". Single-word ids only; revisit if a multi-word
// search-type id is ever added to SEARCH_TYPE_FILTER_IDS.
const toTitleCase = (id) => id.charAt(0) + id.slice(1).toLowerCase();

const SEARCH_TEXT_FILTER_ID = 'SEARCH_TERM_HIT_ENTER_TO_ADD_MULTIPLE';

// --- Sub-Components & Helpers ---
const FilterBox = (props) => {
    const { children } = props;
    const filterRef = useRef(null);
    const divRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    const [showLessMoreAction, setShowLessMoreAction] = useState(false);
    const [showMore, setShowMore] = useState(false);
    const [moreFiltersCount, setMoreFiltersCount] = useState(0);

    const handleSelect = () => setShowMore(!showMore);

    const calculateOverflow = () => {
        if (!filterRef.current || !divRef.current) return;

        const container = filterRef.current;
        const childrenNodes = Array.from(container.children);

        let hiddenCount = 0;
        let isOverflowing = false;

        const firstChild = childrenNodes[0];
        if (!firstChild) {
            setShowLessMoreAction(false);
            return;
        }

        const baseTop = firstChild.offsetTop;

        childrenNodes.forEach((child) => {
            if (child.classList.contains('show-action')) return;

            if (child.offsetTop > baseTop + 10) {
                hiddenCount++;
                isOverflowing = true;
            }
        });

        setShowLessMoreAction(hiddenCount > 0);
        if (showMore && hiddenCount === 0) {}
        setMoreFiltersCount(hiddenCount);
    };

    useLayoutEffect(() => {
        const timer = setTimeout(() => {
            calculateOverflow();
        }, 0);
        return () => clearTimeout(timer);
    }, [children, containerWidth]);

    useLayoutEffect(() => {
        if (!divRef.current) return;

        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setContainerWidth(entry.contentRect.width);
            }
        });

        observer.observe(divRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={divRef} className='filters-applied-container'>
            <div
                ref={filterRef}
                className={classNames({
                    "filters-applied": true,
                    "filters-applied-more": showMore
                })}
            >
                {showLessMoreAction && (
                    <div className='show-action' onClick={handleSelect}>
                        <LinkButton size={'md'}>
                            {!showMore ? `more (${moreFiltersCount})` : 'less'}
                        </LinkButton>
                    </div>
                )}

                {children}
            </div>
        </div>
    );
};

const createAppliedFilters = (filters, handleRemove, restrictions, scopeSuffix = '', accuracyData = null) => {
    const allChips = [];

    if (filters && Object.keys(filters).length > 0) {
        const userChips = map(filters, (filter, idx) => {
            const label = filter.id.replace(/_/g, ' ');
            // const detail = JSON.stringify(filter.value);
            let detail = '';

            if (filter.component === 'geoRadius' && filter.value) {
                // label = '[Radius]';
                const { radius, latitude, longitude } = filter.value;
                detail = `within ${radius} mi from ${latitude}, ${longitude}`;
            } else {
                detail = typeof filter.value === 'object' ? JSON.stringify(filter.value) : String(filter.value);
            }

            if (filter.id === SEARCH_TEXT_FILTER_ID && scopeSuffix) {
                detail = `${detail}${scopeSuffix}`;
            }

            const isLocked = (filter.id && filter.id.startsWith('LOCKED')) || filter.isLocked;

            return (
                <FilterChip
                    key={filter.id || idx}
                    id={filter.id}
                    name={label}
                    detail={detail}
                    type={filter.filterType}
                    handleRemove={handleRemove}
                    isLocked={isLocked}
                    lockOpen={false}
                />
            );
        });

        allChips.push(...userChips);
    }

    if (restrictions) {
        if (restrictions.scores !== undefined && restrictions.scores !== null) {
            // company_score is filtered as a range [score, 1]; show it as such
            // ("BETWEEN 0.8 and 1.0") rather than a single "IS" value.
            const scoreValue = resolveEffectiveScore(accuracyData, restrictions);
            allChips.push(
                <FilterChip
                    key="restriction-score"
                    id="RESTRICTION_SCORE"
                    name="SCORE"
                    detail={`${scoreValue} and 1.0`}
                    type="RANGE"
                    handleRemove={() => {}}
                    isLocked={true}
                    lockOpen={false}
                />
            );
        }
        if (Array.isArray(restrictions.tradeClassifications) && restrictions.tradeClassifications.length > 0) {
            allChips.push(
                <FilterChip
                    key="restriction-trades"
                    id="RESTRICTION_TRADES"
                    name="TRADES"
                    detail={restrictions.tradeClassifications.join(', ')}
                    type="DROP_DOWN"
                    handleRemove={() => {}}
                    isLocked={true}
                    lockOpen={false}
                />
            );
        }
        if (Array.isArray(restrictions.metropolitanStatisticalAreas) && restrictions.metropolitanStatisticalAreas.length > 0) {
            allChips.push(
                <FilterChip
                    key="restriction-msa"
                    id="RESTRICTION_MSA"
                    name="MSA"
                    detail={restrictions.metropolitanStatisticalAreas.join(', ')}
                    type="DROP_DOWN"
                    handleRemove={() => {}}
                    isLocked={true}
                    lockOpen={false}
                />
            );
        }
        if (Array.isArray(restrictions.regions) && restrictions.regions.length > 0) {
            allChips.push(
                <FilterChip
                    key="restriction-region"
                    id="RESTRICTION_REGION"
                    name="STATE"
                    detail={restrictions.regions.join(', ')}
                    type="DROP_DOWN"
                    handleRemove={() => {}}
                    isLocked={true}
                    lockOpen={false}
                />
            );
        }
    }

    return allChips.length > 0 ? allChips : null;
};

export default AppliedFilters;
